/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { fetchGraphQl } from '@/quote-management/api';
import { DELETE_QUOTE_TEMPLATE_MUTATION } from './graphql/deleteQuoteTemplate';
import { state } from '@/quote-management/lib/state';
import { events } from '@adobe-commerce/event-bus';

export interface DeleteQuoteTemplateParams {
  templateId: string;
}

export const deleteQuoteTemplate = async (
  params: DeleteQuoteTemplateParams
) => {
  if (!params.templateId) {
    throw new Error('Template ID is required');
  }

  if (!state.authenticated) {
    throw new Error('Unauthorized');
  }

  try {
    const results: any = await fetchGraphQl(DELETE_QUOTE_TEMPLATE_MUTATION, {
      variables: { templateId: params.templateId },
    });

    if (results?.errors && results.errors.length > 0) {
      const errorMessages = results.errors
        .map((e: any) => e?.message)
        .filter(Boolean)
        .join('; ');
      throw new Error(errorMessages || 'Failed to delete quote template');
    }

    const deleted = results?.data?.deleteNegotiableQuoteTemplate;

    if (!deleted) {
      throw new Error('Failed to delete quote template');
    }

    events.emit('quote-management/quote-template-deleted', {
      templateId: params.templateId,
    });

    return { templateId: params.templateId };
  } catch (error) {
    return Promise.reject(error);
  }
};
